import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import { crmApi, opportunityApi } from '../../services/api';
import {
  ChartBarIcon,
  DocumentTextIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  SparklesIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';

const Dashboard: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [metrics, setMetrics] = useState<any>(null);
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpgradeSuccess, setShowUpgradeSuccess] = useState(false);

  const isFreePlan = user?.plan === 'FREE';
  const isPro = user?.plan === 'PRO' || user?.plan === 'ENTERPRISE';

  useEffect(() => {
    // Check if user just upgraded
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('upgraded') === 'true') {
      setShowUpgradeSuccess(true);
      // Refresh user data to get new plan
      refreshUser();
      // Clear the query param
      window.history.replaceState({}, '', '/dashboard');
    }
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch CRM metrics if Pro/Enterprise
      if (isPro) {
        const [crmRes, recRes] = await Promise.all([
          crmApi.getDashboard().catch(() => null),
          opportunityApi.getRecommendations().catch(() => null),
        ]);
        
        if (crmRes) setMetrics(crmRes.data.metrics);
        if (recRes) setRecommendations(recRes.data.recommendations?.slice(0, 3) || []);
      }
    } catch (error) {
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    {
      name: 'Active Opportunities',
      value: metrics?.pipeline?.totalOpportunities || 0,
      change: '+12%',
      changeType: 'positive',
      icon: DocumentTextIcon,
      proOnly: true,
    },
    {
      name: 'Pipeline Value',
      value: `$${((metrics?.pipeline?.totalValue || 0) / 1000000).toFixed(1)}M`,
      change: '+8%',
      changeType: 'positive',
      icon: CurrencyDollarIcon,
      proOnly: true,
    },
    {
      name: 'Win Rate',
      value: `${metrics?.winRate?.ytd || 0}%`,
      change: '+5%',
      changeType: 'positive',
      icon: ChartBarIcon,
      proOnly: true,
    },
    {
      name: 'Due This Week',
      value: metrics?.upcoming?.dueThisWeek || 0,
      change: '-2',
      changeType: 'negative',
      icon: ClockIcon,
      proOnly: true,
    },
  ];

  const quickActions = [
    { name: 'Find Opportunities', href: '/opportunities', icon: SparklesIcon, color: 'bg-purple-500', free: true },
    { name: 'Ask AI Assistant', href: '/ai-assistant', icon: SparklesIcon, color: 'bg-brand-500', free: false },
    { name: 'Write Proposal', href: '/proposal-writer', icon: DocumentTextIcon, color: 'bg-green-500', free: false },
    { name: 'View Pipeline', href: '/crm', icon: ChartBarIcon, color: 'bg-orange-500', free: false },
  ];

  const proFeatures = [
    { name: 'AI Proposal Writer', desc: 'Generate compliant proposals in minutes', icon: DocumentTextIcon },
    { name: 'GovCon AI Assistant', desc: 'Get instant answers to RFP questions', icon: SparklesIcon },
    { name: 'CRM & Pipeline', desc: 'Track opportunities from lead to win', icon: ChartBarIcon },
    { name: 'Document Hub', desc: 'AI-powered document analysis', icon: DocumentTextIcon },
  ];

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.firstName}!
        </h1>
        <p className="mt-1 text-gray-600">
          Here's what's happening with your GovCon business today.
        </p>
      </div>

      {/* Upgrade Success Banner */}
      {showUpgradeSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-500 rounded-lg p-6 text-white"
        >
          <div className="flex items-center gap-3">
            <SparklesIcon className="h-6 w-6" />
            <div>
              <h2 className="text-xl font-bold">Welcome to Pro!</h2>
              <p className="text-green-100">
                Your subscription is now active. You have access to all Pro features.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Free Plan Upgrade Banner */}
      {isFreePlan && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-brand-600 to-purple-600 rounded-lg p-6 text-white"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <SparklesIcon className="h-6 w-6" />
                Upgrade to Pro & Unlock AI-Powered GovCon Tools
              </h2>
              <p className="mt-2 text-brand-100 max-w-2xl">
                You're currently on the Free plan. Upgrade to Pro to access our AI Proposal Writer, 
                GovCon Assistant, CRM, and more. Win more contracts with less effort.
              </p>
              <div className="mt-4 flex gap-4">
                <Link
                  to="/pricing"
                  className="inline-flex items-center bg-white text-brand-600 px-6 py-2 rounded-lg font-semibold hover:bg-brand-50 transition"
                >
                  View Plans
                </Link>
                <Link
                  to="/billing"
                  className="inline-flex items-center bg-brand-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-brand-400 transition border border-brand-400"
                >
                  Upgrade Now
                </Link>
              </div>
            </div>
            <div className="hidden md:block text-right">
              <p className="text-3xl font-bold">$375</p>
              <p className="text-brand-100">/month</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Stats Grid - Pro Only */}
      {isPro ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="bg-white overflow-hidden shadow rounded-lg"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <stat.icon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{stat.name}</dt>
                      <dd className="flex items-baseline">
                        <div className="text-2xl font-semibold text-gray-900">{stat.value}</div>
                        <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                          stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          <ArrowTrendingUpIcon className="self-center flex-shrink-0 h-4 w-4" />
                          <span className="ml-1">{stat.change}</span>
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        /* Free Plan - Locked Stats Preview */
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="bg-white overflow-hidden shadow rounded-lg opacity-60"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <stat.icon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">{stat.name}</dt>
                      <dd className="flex items-baseline">
                        <div className="text-2xl font-semibold text-gray-400">--</div>
                        <LockClosedIcon className="ml-2 h-4 w-4 text-gray-400" />
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {quickActions.map((action) => {
            const isLocked = !action.free && isFreePlan;
            return (
              <Link
                key={action.name}
                to={isLocked ? '/billing' : action.href}
                className={`flex flex-col items-center p-4 rounded-lg transition-colors relative ${
                  isLocked ? 'bg-gray-100 hover:bg-gray-200' : 'hover:bg-gray-50'
                }`}
              >
                {isLocked && (
                  <div className="absolute top-2 right-2">
                    <LockClosedIcon className="h-4 w-4 text-gray-400" />
                  </div>
                )}
                <div className={`${action.color} p-3 rounded-lg ${isLocked ? 'opacity-50' : ''}`}>
                  <action.icon className="h-6 w-6 text-white" />
                </div>
                <span className={`mt-2 text-sm font-medium ${isLocked ? 'text-gray-500' : 'text-gray-900'}`}>
                  {action.name}
                </span>
                {isLocked && (
                  <span className="text-xs text-brand-600 mt-1">Upgrade</span>
                )}
              </Link>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Pro Features Preview (Free) or Recommendations (Pro) */}
        {isFreePlan ? (
          <div className="bg-white shadow rounded-lg">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Pro Features</h2>
              <p className="text-sm text-gray-500">Upgrade to unlock these powerful tools</p>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {proFeatures.map((feature) => (
                  <div key={feature.name} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="p-2 bg-brand-100 rounded-lg">
                      <feature.icon className="h-5 w-5 text-brand-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-medium text-gray-900">{feature.name}</h3>
                        <LockClosedIcon className="h-3 w-3 text-gray-400" />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link
                to="/pricing"
                className="mt-6 block w-full text-center bg-brand-600 text-white py-2 rounded-lg font-medium hover:bg-brand-500 transition"
              >
                Upgrade to Pro
              </Link>
            </div>
          </div>
        ) : (
          /* Recommended Opportunities - Pro only */
          <div className="bg-white shadow rounded-lg">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">Recommended Opportunities</h2>
                <Link to="/opportunities" className="text-sm text-brand-600 hover:text-brand-500">
                  View all
                </Link>
              </div>
            </div>
            <div className="p-6">
              {recommendations.length > 0 ? (
                <div className="space-y-4">
                  {recommendations.map((opp) => (
                    <div key={opp.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">{opp.title}</h3>
                        <p className="text-xs text-gray-500">{opp.agency}</p>
                      </div>
                      <div className="text-right">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {opp.matchScore}% Match
                        </span>
                        <p className="text-xs text-gray-500 mt-1">${(opp.value / 1000000).toFixed(1)}M</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <SparklesIcon className="mx-auto h-12 w-12 text-gray-300" />
                  <p className="mt-2 text-sm text-gray-500">No recommendations yet</p>
                  <Link to="/opportunities" className="mt-4 text-brand-600 hover:text-brand-500">
                    Search opportunities →
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}

        {/* AI Assistant Preview - Always show but with different CTA */}
        <div className="bg-gradient-to-br from-brand-500 to-purple-600 shadow rounded-lg p-6 text-white">
          <div className="flex items-center mb-4">
            <SparklesIcon className="h-6 w-6" />
            <h2 className="ml-2 text-lg font-medium">GovCon AI Assistant</h2>
            {isFreePlan && <LockClosedIcon className="ml-2 h-4 w-4 text-brand-200" />}
          </div>
          <p className="text-brand-100 mb-4">
            Get instant answers about RFPs, compliance requirements, and proposal writing best practices.
          </p>
          <div className="space-y-3">
            <p className="text-sm text-brand-100">Try asking:</p>
            {[
              'What are the CMMC Level 2 requirements?',
              'Help me write a technical approach',
              'Analyze this RFP for risks',
            ].map((question) => (
              <div
                key={question}
                className="block p-3 bg-white/10 rounded-lg text-sm"
              >
                "{question}"
              </div>
            ))}
          </div>
          {isFreePlan ? (
            <Link
              to="/billing"
              className="mt-4 block text-center bg-white text-brand-600 py-2 rounded-lg font-medium hover:bg-brand-50 transition"
            >
              Upgrade to Access
            </Link>
          ) : (
            <Link
              to="/ai-assistant"
              className="mt-4 block text-center bg-white text-brand-600 py-2 rounded-lg font-medium hover:bg-brand-50 transition"
            >
              Start Chatting
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
